import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useVisite } from '@offertify/query';
import { visiteAPI } from '@offertify/api-client';
import { usersAPI } from '../utils/api';
import { format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Calendar, Download, Loader2, ChevronDown, LayoutGrid, CalendarDays } from 'lucide-react';
import AttivitaCalendar from '../components/attivita/AttivitaCalendar';
import CalendarActivityForm from '../components/attivita/CalendarActivityForm';
import MultiSelectFilter from '../components/attivita/MultiSelectFilter';
import { statoConfig, clienteName, venditoreName, INTERNAL_CATEGORIES } from '../components/attivita/attivitaConfig';
import { useActivityTypes, resolveActivityType } from '../hooks/useActivityTypes';
import type { Visita } from '../types';
import { usePermissions } from '../hooks/usePermissions';

type ViewMode = 'grid' | 'calendar';
const VIEW_STORAGE_KEY = 'attivita_view';

function loadView(): ViewMode {
  if (typeof window === 'undefined') return 'grid';
  return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'calendar' ? 'calendar' : 'grid';
}

function formatData(value: string): string {
  try {
    return format(parseISO(value), 'dd/MM/yyyy', { locale: it });
  } catch {
    return value;
  }
}

export default function Attivita() {
  const { hasPermission } = usePermissions();
  const canExport = hasPermission('visite:export');
  const canCreate = hasPermission('visite:create');
  const canEdit = hasPermission('visite:edit');
  const [editing, setEditing] = useState<Visita | undefined>();
  // URL is the source of truth, including navigation between dashboard widgets.
  const [searchParams, setSearchParams] = useSearchParams();
  const setFilter = (key: string, value: string) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value); else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  };
  const fromDate = searchParams.get('from_date') || '';
  const toDate = searchParams.get('to_date') || '';
  const stati = searchParams.get('stato')?.split(',').filter(Boolean) || [];
  const tipi = searchParams.get('tipo')?.split(',').filter(Boolean) || [];
  const venditoreIds = searchParams.get('user_id')?.split(',').filter(Boolean) || [];
  const origine = searchParams.get('origine') || '';
  const interne = searchParams.get('interne') || '';
  const setFromDate = (value: string) => setFilter('from_date', value);
  const setToDate = (value: string) => setFilter('to_date', value);
  const setStati = (value: string[]) => setFilter('stato', value.join(','));
  const setTipi = (value: string[]) => setFilter('tipo', value.join(','));
  const setVenditoreIds = (value: string[]) => setFilter('user_id', value.join(','));
  const setOrigine = (value: string) => setFilter('origine', value);
  const setInterne = (value: string) => setFilter('interne', value);
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const setPage = (value: number) => setFilter('page', String(value));
  const [isExporting, setIsExporting] = useState(false);
  const view: ViewMode = searchParams.get('view') === 'grid' ? 'grid' : searchParams.get('view') === 'calendar' ? 'calendar' : loadView();
  // Visible month of the calendar view, owned here so the export can follow it.
  const [calMonth, setCalMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [calendarCreateDate, setCalendarCreateDate] = useState<string | null>(null);
  const limit = 20;

  const changeView = (next: ViewMode) => {
    setFilter('view', next);
    if (typeof window !== 'undefined') window.localStorage.setItem(VIEW_STORAGE_KEY, next);
  };

  // The window the calendar actually shows/fetches (whole weeks around the month).
  // Kept in sync with AttivitaCalendar's own grid math so the export matches it.
  const calWindow = useMemo(
    () => ({
      from_date: format(startOfWeek(startOfMonth(calMonth), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      to_date: format(endOfWeek(endOfMonth(calMonth), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    }),
    [calMonth],
  );

  const filters = useMemo(
    () => ({
      dashboard_only: searchParams.get('dashboard_only') === 'true' || undefined,
      kpi: searchParams.get('kpi') || undefined,
      reference_date: searchParams.get('reference_date') || undefined,
      exclude_cancelled: searchParams.get('exclude_cancelled') === 'true' || undefined,
      cliente_id: searchParams.get('cliente_id') || undefined,
      origine: origine ? origine as 'app' | 'manuale' : undefined,
      interne: interne ? interne === 'true' : undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
      stato: stati.length ? stati.join(',') : undefined,
      tipo: tipi.length ? tipi.join(',') : undefined,
      user_id: venditoreIds.length ? venditoreIds.join(',') : undefined,
    }),
    [fromDate, toDate, stati, tipi, venditoreIds, origine, interne, searchParams],
  );

  const { data, isLoading, isFetching } = useVisite({ page, limit, ...filters });
  const visite = data?.data || [];
  const pagination = data?.pagination;
  const hasMore = pagination ? page < pagination.pages : false;

  // Tipi attività dall'anagrafica (contesto 'cliente': copre tutti i codici
  // selezionabili su visite — visita/telefonata/videochiamata/email/riunione/nota).
  const { data: activityTypes } = useActivityTypes('cliente');

  // Venditori for the filter dropdown, scoped al proprio cono di visibilità
  // (issue #54) per chi non ha il permesso users:view — stesso endpoint di Leads.tsx.
  const { data: agents } = useQuery({
    queryKey: ['users-hierarchy'],
    queryFn: async () => {
      const res = await usersAPI.getHierarchy();
      return res.data;
    },
  });
  const venditori = (agents || []).filter((u) => u.is_active);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // In calendar mode the Dal/Al inputs are hidden, so export the month on
      // screen instead of the (hidden, possibly stale) grid-mode date filters.
      const exportFilters =
        view === 'calendar'
          ? { ...filters, from_date: filters.dashboard_only && fromDate > calWindow.from_date ? fromDate : calWindow.from_date, to_date: filters.dashboard_only && toDate && toDate < calWindow.to_date ? toDate : calWindow.to_date }
          : filters;
      const res = await visiteAPI.exportVisite(exportFilters);
      const disposition = (res.headers as Record<string, string>)['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `attivita-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Excel scaricato: ${filename}`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Errore nell'export");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attività</h1>
          <p className="text-sm text-gray-500">Visite e attività di tutti i venditori</p>
        </div>
        <div className="flex items-center gap-3">
          {canCreate && <button onClick={() => setCalendarCreateDate(format(new Date(), 'yyyy-MM-dd'))} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white">Nuova attività</button>}
          {/* View toggle: grid (table) vs calendar */}
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5 bg-white">
            <button
              onClick={() => changeView('grid')}
              aria-pressed={view === 'grid'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                view === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
              Griglia
            </button>
            <button
              onClick={() => changeView('calendar')}
              aria-pressed={view === 'calendar'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                view === 'calendar' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <CalendarDays className="h-4 w-4" />
              Calendario
            </button>
          </div>
          {canExport && (
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Esporta Excel
            </button>
          )}
        </div>
      </div>

      {(filters.kpi || filters.cliente_id || filters.exclude_cancelled) && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm flex justify-between gap-3">
          <span>{filters.kpi === 'tasso' ? 'Attività completate e annullate usate per il tasso di completamento' : filters.kpi ? ({ oggi: 'Attività di oggi', prossime7: 'Programmate nei prossimi 7 giorni', completate_mese: 'Completate nel mese', in_ritardo: 'Programmate in ritardo', follow_up: 'Follow-up nei prossimi 7 giorni', completate: 'Attività completate' } as Record<string, string>)[filters.kpi] : 'Attività del cliente selezionato'}{filters.exclude_cancelled ? ' · escluse le annullate' : ''}</span>
          <button onClick={() => setSearchParams((previous) => { const next = new URLSearchParams(previous); ['kpi', 'reference_date', 'cliente_id', 'exclude_cancelled', 'page'].forEach((key) => next.delete(key)); return next; })} className="text-blue-700 underline">Rimuovi selezione dashboard</button>
        </div>
      )}
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {view === 'grid' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Dal</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Al</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </>
        )}
        <MultiSelectFilter
          label="Venditore"
          allLabel="Tutti i venditori"
          options={venditori.map((u) => ({
            value: u.id,
            label: `${u.nome || ''} ${u.cognome || ''}`.trim() || u.name || u.email,
          }))}
          selected={venditoreIds}
          onChange={(next) => setVenditoreIds(next)}
        />
        <MultiSelectFilter
          label="Stato"
          allLabel="Tutti gli stati"
          options={[
            { value: 'programmata', label: 'Programmate' },
            { value: 'completata', label: 'Completate' },
            { value: 'annullata', label: 'Annullate' },
            { value: 'rinviata', label: 'Rinviate' },
          ]}
          selected={stati}
          onChange={(next) => setStati(next)}
        />
        <MultiSelectFilter
          label="Tipo"
          allLabel="Tutti i tipi"
          options={[...(activityTypes || []).map((t) => ({ value: t.codice, label: t.label })), ...Object.entries(INTERNAL_CATEGORIES).filter(([code]) => !(activityTypes || []).some((t) => t.codice === code)).map(([value, label]) => ({ value, label }))]}
          selected={tipi}
          onChange={(next) => setTipi(next)}
        />
        <div><label htmlFor="activity-origin" className="block text-xs font-medium">Origine</label><select id="activity-origin" value={origine} onChange={(e) => setOrigine(e.target.value)} className="w-full rounded-lg border p-2"><option value="">Tutte</option><option value="manuale">Manuale</option><option value="app">Richieste da app</option></select></div>
        <div><label htmlFor="activity-context" className="block text-xs font-medium">Ambito</label><select id="activity-context" value={interne} onChange={(e) => setInterne(e.target.value)} className="w-full rounded-lg border p-2"><option value="">Tutte le attività</option><option value="false">Commerciali</option><option value="true">Interne</option></select></div>
      </div>

      {view === 'calendar' ? (
        <AttivitaCalendar
          dashboardOnly={filters.dashboard_only}
          drilldown={{ kpi: filters.kpi, reference_date: filters.reference_date, exclude_cancelled: filters.exclude_cancelled, cliente_id: filters.cliente_id }}
          windowFrom={filters.dashboard_only ? filters.from_date : undefined}
          windowTo={filters.dashboard_only ? filters.to_date : undefined}
          tipo={tipi.length ? tipi.join(',') : undefined}
          origine={filters.origine}
          interne={filters.interne}
          onEdit={canEdit ? setEditing : undefined}
          stato={stati.length ? stati.join(',') : undefined}
          userId={venditoreIds.length ? venditoreIds.join(',') : undefined}
          month={calMonth}
          onMonthChange={setCalMonth}
          canCreate={canCreate}
          onCreate={setCalendarCreateDate}
        />
      ) : (
        <>
      {/* Results count */}
      {pagination && (
        <div className="text-sm text-gray-500">
          {pagination.total} attività trovate
          {isFetching && <Loader2 className="inline h-3.5 w-3.5 animate-spin ml-2 text-blue-600" />}
        </div>
      )}
      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Cliente / Ambito', 'Venditore', 'Data', 'Ora', 'Tipo', 'Stato', 'Esito', 'Azioni'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading && page === 1 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : visite.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    Nessuna attività trovata con i filtri selezionati
                  </td>
                </tr>
              ) : (
                visite.map((visita) => {
                  const tipo = resolveActivityType(visita.tipo, activityTypes);
                  const TipoIcon = tipo.icon;
                  const st = statoConfig(visita.stato);
                  return (
                    <tr key={visita.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {clienteName(visita)}
                        {visita.serie_id && <span className="ml-2 text-xs text-purple-700">{visita.is_eccezione ? 'Eccezione serie' : 'Ricorrente'}</span>}
                        {visita.origine === 'app' && <span className="ml-2 text-xs text-blue-600">Richieste da app</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {venditoreName(visita)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {formatData(visita.data)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {visita.ora ? visita.ora.substring(0, 5) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${tipo.colore}1A`, color: tipo.colore }}
                        >
                          <TipoIcon className="h-3.5 w-3.5" />
                          {tipo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}
                        >
                          {st.icon}
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {visita.esito || '—'}
                      </td>
                      <td className="px-4 py-3">{canEdit && <button className="text-sm text-blue-600" onClick={() => setEditing(visita)}>Modifica</button>}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Precedente
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-500">
            Pagina {page} di {pagination.pages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={!hasMore}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Successiva
            <ChevronDown className="h-3 w-3 -rotate-90" />
          </button>
        </div>
      )}
        </>
      )}
      {editing && <CalendarActivityForm visita={editing} initialDate={editing.data} onClose={() => setEditing(undefined)} onSuccess={() => setEditing(undefined)} />}
      {calendarCreateDate && (
        <CalendarActivityForm
          initialDate={calendarCreateDate}
          onClose={() => setCalendarCreateDate(null)}
          onSuccess={() => setCalendarCreateDate(null)}
        />
      )}
    </div>
  );
}
